import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { SquareLinkedService, squareReservedKeys } from 'app/model/linkedservice/square.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SquareLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                host: '*',
                clientId: '*',
                clientSecret: 'secret',
                redirectUri: '*',
                accessToken: 'secret',
                refreshToken: 'secret',
                useEncryptedEndpoints: '*',
                useHostVerification: '*',
                usePeerVerification: '*',
                encryptedCredential: '*',
                connectionProperties: {
                    host: '*',
                    clientId: '*',
                    clientSecret: 'secret',
                    accessToken: 'secret',
                    refreshToken: 'secret',
                    useEncryptedEndpoints: '*',
                    useHostVerification: '*',
                    usePeerVerification: '*',
                    encryptedCredential: '*'
                }
            }
        }
    }
})
export class SquareLinkedServiceAdapter extends LinkedServiceAdapter<SquareLinkedService> {
    protected modelConstructor(): new () => SquareLinkedService {
        return SquareLinkedService;
    }

    protected model(): IModel {
        return SquareLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: SquareLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SquareLinkedService;
        properties.type = Metamodel.modelFor(SquareLinkedService).name;
        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            properties.typeProperties = this._buildTypeProperties(linkedService);
        } else if (linkedService.isOldResource) {
            properties.typeProperties = {
                host: linkedService.host,
                clientId: linkedService.clientId,
                clientSecret: passwordOrAKVtoSecretBase(linkedService.clientSecret, this.context),
                redirectUri: linkedService.redirectUri,
                useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
                useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
                usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
            };
        } else {
            properties.typeProperties = {
                connectionProperties: this._buildTypeProperties(linkedService)
            };
            if (!arrayIsNullOrEmpty(linkedService.additionalFields)) {
                linkedService.additionalFields.forEach((field) => {
                    properties.typeProperties.connectionProperties[field.key] = field.value;
                });
            }
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: SquareLinkedService): Promise<SquareLinkedService> {
        const properties = linkedServiceResource.properties as Model.SquareLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.SquareLinkedServiceTypeProperties = properties.typeProperties;
        linkedService.version = properties.version === CONNECTOR_VERSION20 ? driverVersions.v2 : driverVersions.v1;
        linkedService.isLegacyConnector = linkedService.version !== driverVersions.v2;
        if (linkedService.version === driverVersions.v2) {
            linkedService.isOldResource = false;
            linkedService.host = typeProperties.host;
            linkedService.clientId = typeProperties.clientId;
            linkedService.clientSecret = secretBaseToPasswordOrAKV(typeProperties.clientSecret, true, this.context);
            linkedService.refreshToken = secretBaseToPasswordOrAKV(typeProperties.refreshToken, true, this.context);
            linkedService.accessToken = secretBaseToPasswordOrAKV(typeProperties.accessToken, true, this.context);
            linkedService.useEncryptedEndpoints = typeProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = typeProperties.useHostVerification;
            linkedService.usePeerVerification = typeProperties.usePeerVerification;
        } else if (typeProperties.connectionProperties) {
            linkedService.isOldResource = false;
            const connectionProperties = typeProperties.connectionProperties;
            linkedService.host = connectionProperties.host;
            linkedService.clientId = connectionProperties.clientId;
            linkedService.clientSecret = secretBaseToPasswordOrAKV(connectionProperties.clientSecret, true, this.context);
            linkedService.refreshToken = secretBaseToPasswordOrAKV(connectionProperties.refreshToken, true, this.context);
            linkedService.accessToken = secretBaseToPasswordOrAKV(connectionProperties.accessToken, true, this.context);
            linkedService.useEncryptedEndpoints = connectionProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = connectionProperties.useHostVerification;
            linkedService.usePeerVerification = connectionProperties.usePeerVerification;
            linkedService.additionalFields = [];
            Object.keys(connectionProperties).forEach((key) => {
                if (squareReservedKeys.indexOf(key) < 0) {
                    linkedService.additionalFields.push({ key, value: connectionProperties[key] });
                    delete connectionProperties[key];
                }
            });
        } else {
            linkedService.isOldResource = true;
            linkedService.host = typeProperties.host;
            linkedService.clientId = typeProperties.clientId;
            linkedService.clientSecret = secretBaseToPasswordOrAKV(typeProperties.clientSecret, true, this.context);
            linkedService.redirectUri = typeProperties.redirectUri;
            linkedService.useEncryptedEndpoints = typeProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = typeProperties.useHostVerification;
            linkedService.usePeerVerification = typeProperties.usePeerVerification;
        }
        return linkedService;
    }
    private _buildTypeProperties(linkedService: SquareLinkedService) {
        return {
            host: linkedService.host,
            clientId: linkedService.clientId,
            clientSecret: passwordOrAKVtoSecretBase(linkedService.clientSecret, this.context),
            refreshToken: passwordOrAKVtoSecretBase(linkedService.refreshToken, this.context),
            accessToken: passwordOrAKVtoSecretBase(linkedService.accessToken, this.context),
            useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
            useHostVerification:
                linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
            usePeerVerification:
                linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
        };
    }
}
